// AtriumManagerHome.tsx — Atrium Manager/Admin home, "home" (Sprint 84, ADR-082).
// ----------------------------------------------------------------------------
// The Salesforce-style manager home, composed from the Sprint 83 home framework
// and fed REAL scoped data from condoStore + the dashboard stats snapshot. It is
// the new-shell replacement for the legacy OverviewComponent when the
// `atriumRedesign` flag is on for an admin/manager.
//
// HONESTY CONTRACT (ADR-082): every KPI chip, stat, and chart series is sourced
// from a real store field. Where the prototype showed a metric we have NO real
// backing for (operating cash, reserve fund), the chip is OMITTED rather than
// fabricated. Anything sample (Tasks) is clearly labelled. A failed fetch
// surfaces an honest error banner (Sprint 81 contract), never a false $0.
import React, { useEffect, useMemo } from 'react';
import { Skeleton, Button } from 'antd';
import {
  PlusOutlined,
  BarChartOutlined,
  ToolOutlined,
  DollarOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { useAuthStore } from '../../../store/authStore';
import { useCondoStore } from '../../../store/condoStore';
import type { WorkOrder } from '../../../types';

import { useAtriumNav } from '../atriumNav';
import { SFGreeting, type SFKpiChip } from './SFGreeting';
import { AtriumAreaChart } from './AreaChart';
import { Assistant, type AssistantItem } from './Assistant';
import './homeStyles.css';
import { currency as currencyFmt } from '../../../utils/currency';

// Portfolio/KPI sums read cleaner without cents; delegate to the one CAD helper.
const currency = (n: number) => currencyFmt(n, { cents: false });

/** Work-order status → badge tone + label (REAL enum, no string-compare bypass). */
function woBadge(status: WorkOrder['status']): { tone: string; label: string } {
  switch (status) {
    case 'OPEN':        return { tone: 'green', label: 'Open' };
    case 'IN_PROGRESS': return { tone: 'blue',  label: 'In progress' };
    case 'COMPLETED':   return { tone: 'gray',  label: 'Completed' };
    case 'CANCELLED':   return { tone: 'gray',  label: 'Cancelled' };
    default:            return { tone: 'amber', label: String(status) };
  }
}

/**
 * Sprint 144 (R14-25, ADR-139) — the greeting was the literal string
 * "Good morning", so it read "Good morning" at 22:20 local. Branch on the
 * viewer's local hour so the claim is true, rather than dropping the greeting:
 * the time-of-day form is fine, the hardcoding was not. Boundaries follow the
 * common convention (morning < 12:00, afternoon < 17:00, evening after).
 */
export function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const woPriorityColor: Record<string, string> = {
  URGENT: 'var(--red)',
  HIGH:   'var(--amber)',
  MEDIUM: 'var(--blue)',
  LOW:    'var(--mut2)',
};

export const AtriumManagerHome: React.FC = () => {
  const { user, currentCondo } = useAuthStore();
  const { go } = useAtriumNav();
  const {
    getDashboardStats,
    fetchOwners,
    fetchFees,
    fetchPayments,
    fetchWorkOrders,
    fetchViolations,
    fetchUnits,
    workOrders,
    violations,
    units,
    payments,
    fees,
    owners,
    getOverdueFees,
    getOutstandingTotal,
    getUnitStats,
    error,
    loadingByKey,
    errorByKey,
  } = useCondoStore();

  // Sprint 106 (106.2) — gate the KPI hero chips + money stat strip on the
  // home's first relevant fetch so an admin never sees a blank/$0 dashboard
  // before fees/owners/payments/units settle.
  const RELEVANT_KEYS = ['fees', 'owners', 'payments', 'units'] as const;
  const summaryLoading =
    RELEVANT_KEYS.some(k => loadingByKey[k] === true) ||
    RELEVANT_KEYS.every(k => loadingByKey[k] === undefined);
  const summaryError = RELEVANT_KEYS.map(k => errorByKey[k]).find(e => e != null) ?? null;
  const retrySummary = () => {
    if (!currentCondo?.id) return;
    fetchOwners(currentCondo.id);
    fetchFees(currentCondo.id);
    fetchPayments(currentCondo.id);
    fetchUnits(currentCondo.id);
  };

  // Reuse EXISTING store actions only — same scoped fetches the dashboard runs.
  useEffect(() => {
    if (currentCondo?.id) {
      fetchOwners(currentCondo.id);
      fetchFees(currentCondo.id);
      fetchPayments(currentCondo.id);
      fetchWorkOrders(currentCondo.id);
      fetchViolations(currentCondo.id);
      fetchUnits(currentCondo.id);
    }
  }, [currentCondo?.id, fetchOwners, fetchFees, fetchPayments, fetchWorkOrders, fetchViolations, fetchUnits]);

  const condoId = currentCondo?.id;

  // REAL — the same dashboard snapshot the legacy overview consumes. units +
  // payments + owners + fees are intentional deps: getDashboardStats reads
  // those live store arrays, so the snapshot must recompute when they change.
  // R11-6 — owners/fees were missing, so when the owners fetch settled after
  // the last units/payments change the memo never recomputed and the header
  // showed "0 residents" (and Pending-fees had the same staleness).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stats = useMemo(() => getDashboardStats(condoId), [getDashboardStats, condoId, units, payments, owners, fees]);

  // REAL — condo-scoped work orders, open ones first.
  const condoWorkOrders = useMemo(
    () => workOrders.filter(w => w.condoId === condoId),
    [workOrders, condoId],
  );
  const openWorkOrders = condoWorkOrders.filter(
    w => w.status !== 'COMPLETED' && w.status !== 'CANCELLED',
  );

  // REAL — condo-scoped active violations.
  const activeViolations = useMemo(
    () => violations.filter(
      v => v.condoId === condoId && (v.status === 'open' || v.status === 'notice_sent'),
    ),
    [violations, condoId],
  );

  // REAL — overdue fees for collections signal. payments is an intentional dep:
  // getOverdueFees reads the live payments array to determine what's still due.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const overdueFees = useMemo(() => getOverdueFees(condoId), [getOverdueFees, condoId, payments]);
  const overdueTotal = overdueFees.reduce((s, f) => s + f.amount, 0);

  // REAL — TOTAL unpaid dues (PENDING + OVERDUE) for the "Outstanding dues" KPI.
  // fees is the intentional dep: getOutstandingTotal reads the live fees array,
  // so the value recomputes whenever fees change (e.g. after a mark-paid).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const outstandingTotal = useMemo(() => getOutstandingTotal(condoId), [getOutstandingTotal, condoId, fees]);

  // REAL — occupancy-by-building. We manage ONE active condo at a time
  // (currentCondo), so "by building" is the active property's unit occupancy.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- units is intentional: getUnitStats reads the live units array.
  const unitStats = useMemo(() => getUnitStats(condoId), [getUnitStats, condoId, units]);

  // REAL chart series + headline totals: revenue collected per month over the
  // last 6 months, derived from the completed-payments ledger (mirrors
  // AtriumOwnerHome's ledger-derived approach). No mock array.
  //
  // Sprint 134 (E2E Round 9, M-1): the headline "Revenue collected" number, the
  // "Quarter revenue" chip, and the sub-line previously read
  // stats.totalRevenue / stats.netIncome — which come from the `revenues`
  // collection this page never fetches, so they were a hard $0 while this very
  // chart showed real collected payments. They now derive from the SAME
  // completed-payments ledger as the chart, so the number matches the bars.
  // Net income needs expenses we don't fetch, so it is omitted (honesty
  // contract) in favour of a real "collected this quarter" figure.
  const { chartPoints, chartLabels, hasChartData, sixMonthCollected, quarterCollected } = useMemo(() => {
    const now = new Date();
    const buckets: { label: string; total: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ label: d.toLocaleString('en-CA', { month: 'short' }), total: 0 });
    }
    // Current calendar-quarter bounds, for the "Quarter revenue" chip/sub-line.
    const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const qEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 1);
    let quarter = 0;
    payments
      .filter(p => p.condoId === condoId && p.status === 'completed')
      .forEach(p => {
        const d = new Date(p.paymentDate);
        const monthsAgo = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
        if (monthsAgo >= 0 && monthsAgo <= 5) buckets[5 - monthsAgo].total += p.amount;
        if (d >= qStart && d < qEnd) quarter += p.amount;
      });
    return {
      chartPoints: buckets.map(b => b.total),
      chartLabels: buckets.map(b => b.label),
      hasChartData: buckets.some(b => b.total > 0),
      sixMonthCollected: buckets.reduce((s, b) => s + b.total, 0),
      quarterCollected: quarter,
    };
  }, [payments, condoId]);

  // ── KPI chips — REAL fields only. Operating cash + reserve fund have NO
  // backing source in the current data model, so they are OMITTED (not faked). ──
  // 106.2 — until the first relevant fetch settles (or on failure), show "—"
  // placeholders rather than a confident $0 KPI.
  const chips: SFKpiChip[] = (summaryLoading || summaryError)
    ? [
        { l: 'Quarter revenue', v: '—' },
        { l: 'Outstanding dues', v: '—' },
        { l: 'Collection rate', v: '—' },
        { l: 'Occupancy', v: '—' },
      ]
    : [
        { l: 'Quarter revenue', v: currency(quarterCollected) },
        { l: 'Outstanding dues', v: currency(outstandingTotal) },
        { l: 'Collection rate', v: `${stats.feeCollectionRate}%` },
        { l: 'Occupancy', v: `${Math.round(stats.occupancyRate)}%` },
      ];

  // Assistant — derived from REAL signals.
  const assistantItems: AssistantItem[] = [];
  if (overdueFees.length > 0) {
    assistantItems.push({
      tone: 'red',
      h: `${overdueFees.length} overdue fee(s) in collections`,
      // Sprint 138 — R12-15: this value is `overdueTotal` (overdue subtotal), not
      // the wider `outstandingTotal` shown in the "Outstanding dues" KPI above.
      // Say "overdue" so the two figures don't read as a contradiction.
      s: `${currency(overdueTotal)} overdue across the property`,
      action: 'Review',
    });
  }
  if (activeViolations.length > 0) {
    assistantItems.push({
      tone: 'amber',
      h: `${activeViolations.length} active violation(s)`,
      s: 'Open or notice-sent on this property',
      action: 'Open',
    });
  }
  openWorkOrders.slice(0, 2).forEach(w =>
    assistantItems.push({
      tone: 'blue',
      h: `Work order: ${w.title}`,
      s: `${w.unitNumber ? `Unit ${w.unitNumber} · ` : ''}${woBadge(w.status).label}`,
      action: 'Track',
    }),
  );

  const managerName = user?.name?.split(' ')[0] || 'there';
  // Read at render time (the home remounts on navigation / condo switch); no
  // ticking timer, which would re-render the whole home once a minute for a
  // string that changes three times a day.
  const greeting = greetingForHour(new Date().getHours());

  return (
    <div className="wpage wide fadein">
      <SFGreeting
        hi={`${greeting}, ${managerName}`}
        badge="MANAGER"
        sub={<span>{currentCondo?.name ?? 'No property selected'} · {stats.totalOwners} residents</span>}
        chips={chips}
        btns={
          /* Sprint 144 — both CTAs were rendered with NO onClick at all: two
             more controls that appear to do something and don't. Wired to the
             shell navigator (the same seam Sprint 88B used to revive the owner
             home's dead CTAs) rather than deleted, because both have a real
             destination page already in this user's nav. */
          <>
            <button className="hb ghost" type="button" onClick={() => go('reports-analytics')}>
              <BarChartOutlined /> Reports
            </button>
            <button className="hb solid" type="button" onClick={() => go('maintenance-hub')}>
              <PlusOutlined /> New work order
            </button>
          </>
        }
      />

      {summaryError ? (
        <div className="home-error" role="alert">
          We couldn't load some property data ({summaryError}).{' '}
          <Button size="small" onClick={retrySummary} style={{ marginLeft: 8 }}>Retry</Button>
        </div>
      ) : error ? (
        <div className="home-error" role="alert">
          We couldn't load some property data ({error}). The figures below may be
          incomplete — please retry shortly.
        </div>
      ) : null}

      <div className="sfgrid">
        <div className="sfcol">
          {/* Performance chart — REAL collected-revenue-per-month series */}
          <section className="c p perf" aria-labelledby="m-revenue-h">
            <div className="pf-top">
              <div>
                <div className="ch" style={{ marginBottom: 0 }}><h2 id="m-revenue-h">Revenue collected · last 6 months</h2></div>
                <div className="pf-big num">{currency(sixMonthCollected)}</div>
                <div className="pf-goal">
                  {hasChartData ? (
                    <>Collected this quarter <b>{currency(quarterCollected)}</b></>
                  ) : (
                    <span className="home-note">No payment history yet for this property</span>
                  )}
                </div>
              </div>
            </div>
            <AtriumAreaChart
              points={chartPoints}
              labels={chartLabels}
              color="#2563EB"
              valueFormatter={currency}
              ariaLabel={
                hasChartData
                  ? `Revenue collected over the last 6 months: ${chartLabels
                      .map((m, i) => `${m} ${currency(chartPoints[i])}`)
                      .join(', ')}.`
                  : 'Revenue collected over the last 6 months: no payment history yet for this property.'
              }
            />
          </section>

          {/* Work order queue — REAL */}
          <section className="c p" aria-labelledby="m-workorders-h">
            <div className="ch">
              <h2 id="m-workorders-h">Work order queue</h2>
              <span className="bd blue"><span className="pd" />{openWorkOrders.length} open</span>
            </div>
            {openWorkOrders.length === 0 && (
              <div className="rempty" style={{ padding: '12px 0' }}>No open work orders. All clear.</div>
            )}
            {openWorkOrders.slice(0, 5).map(w => {
              const b = woBadge(w.status);
              return (
                <div className="rec-row" key={w.id}>
                  <span className="ricon" style={{ background: woPriorityColor[w.priority ?? 'LOW'] ?? 'var(--mut2)' }}>
                    <ToolOutlined />
                  </span>
                  <div className="rmain">
                    <div className="h">{w.title}</div>
                    <div className="s">
                      {w.unitNumber ? `Unit ${w.unitNumber} · ` : ''}{w.vendorName ?? 'Unassigned'}
                    </div>
                  </div>
                  <span className={'bd ' + b.tone}><span className="pd" />{b.label}</span>
                </div>
              );
            })}
          </section>

          {/* Occupancy by building — REAL unit stats for the active property */}
          <section className="c p" aria-labelledby="m-occupancy-h">
            <div className="ch">
              <h2 id="m-occupancy-h">Occupancy · {currentCondo?.name ?? 'property'}</h2>
              <span className="lk" style={{ cursor: 'default' }}>
                {unitStats.occupiedUnits}/{unitStats.totalUnits} units
              </span>
            </div>
            {unitStats.totalUnits === 0 ? (
              <div className="rempty" style={{ padding: '12px 0' }}>No units on file for this property.</div>
            ) : (
              <div style={{ marginBottom: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, marginBottom: 7 }}>
                  <span>{currentCondo?.name}</span>
                  <span style={{ color: 'var(--mut)' }}>{unitStats.totalUnits} units · {Math.round(unitStats.occupancyRate)}%</span>
                </div>
                <div style={{ height: 8, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${unitStats.occupancyRate}%`, height: '100%', background: 'var(--blue)' }} />
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Right rail */}
        <div className="sfcol">
          <Assistant items={assistantItems} />

          {/* Quick stat strip — REAL. Gated on first load (106.2): show a
              skeleton rather than 0 residents / 0 pending fees before data. */}
          {summaryLoading && !summaryError ? (
            <div className="c p"><Skeleton active paragraph={{ rows: 3 }} /></div>
          ) : (
          <section className="statrow" aria-label="Property summary" style={{ gridTemplateColumns: 'repeat(2,1fr)' }}>
            <div className="stat">
              <div className="gi"><TeamOutlined /></div>
              <div className="v">{stats.totalOwners}</div>
              <div className="l">Residents</div>
            </div>
            <div className="stat">
              <div className="gi"><ToolOutlined /></div>
              <div className="v">{openWorkOrders.length}</div>
              <div className="l">Open work orders</div>
            </div>
            <div className="stat">
              <div className="gi"><WarningOutlined /></div>
              <div className="v">{activeViolations.length}</div>
              <div className="l">Active violations</div>
            </div>
            <div className="stat">
              <div className="gi"><DollarOutlined /></div>
              <div className="v">{stats.pendingFees}</div>
              <div className="l">Pending fees</div>
            </div>
          </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AtriumManagerHome;
